import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { supabase } from '@/integrations/supabase/client';
import { audioWorker } from '@/workers/AudioGenerationWorker';
import { 
  Play, 
  Square, 
  CheckCircle2, 
  XCircle, 
  Clock, 
  DollarSign,
  Music,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

interface AudioStats {
  total: number;
  withAudio: number;
  missing: number;
  percentage: number;
}

interface CourseStats {
  courseId: string;
  courseTitle: string;
  totalModules: number;
  withAudio: number;
  missing: number;
}

interface ProgressUpdate {
  processed: number;
  successful: number;
  failed: number;
  total: number;
  percentage: number;
}

export const AudioGenerationDashboard: React.FC = () => {
  const [stats, setStats] = useState<AudioStats | null>(null);
  const [courseStats, setCourseStats] = useState<CourseStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState<ProgressUpdate | null>(null);
  const [errorLog, setErrorLog] = useState<string[]>([]);
  const [estimatedCost, setEstimatedCost] = useState<number>(0);

  useEffect(() => {
    loadStats();
  }, []);

  useEffect(() => {
    checkExistingOperation();
  }, []);

  const checkExistingOperation = async () => {
    const { data: operations } = await supabase
      .from('audio_generation_operations')
      .select('*')
      .eq('status', 'in_progress')
      .order('started_at', { ascending: false })
      .limit(1);

    if (operations && operations.length > 0) {
      const op = operations[0];
      setGenerating(true);
      setProgress({
        processed: op.processed_items || 0,
        successful: op.successful_items || 0,
        failed: op.failed_items || 0,
        total: op.total_items || 0,
        percentage: op.total_items ? Math.round(((op.processed_items || 0) / op.total_items) * 100) : 0
      });
      
      await audioWorker.start(handleProgress);
      toast.info('Resuming audio generation in progress');
    }
  };

  const loadStats = async () => {
    try {
      setLoading(true);

      // Get total ACTIVE modules from course_modules (is_enabled = true)
      const { count: totalCount } = await supabase
        .from('course_modules')
        .select('module_id', { count: 'exact', head: true })
        .eq('is_enabled', true);

      // Get module IDs for enabled modules
      const { data: enabledModules } = await supabase
        .from('course_modules')
        .select('module_id')
        .eq('is_enabled', true);

      const moduleIds = enabledModules?.map(m => m.module_id) || [];

      // Get modules with audio from the enabled set
      const { data: audioData } = await supabase
        .from('module_audio')
        .select('module_id, audio_url')
        .in('module_id', moduleIds);

      const withAudio = audioData?.filter(a => a.audio_url).length || 0;
      const total = totalCount || 0;
      const missing = total - withAudio;

      setStats({
        total,
        withAudio,
        missing,
        percentage: total > 0 ? Math.round((withAudio / total) * 100) : 0
      });

      const avgCharsPerModule = 2000;
      const costPer1MChars = 15;
      const estimatedChars = missing * avgCharsPerModule;
      const cost = (estimatedChars / 1000000) * costPer1MChars;
      setEstimatedCost(cost);

      await loadCourseStats();

    } catch (error) {
      console.error('Error loading stats:', error);
      toast.error('Failed to load audio statistics');
    } finally {
      setLoading(false);
    }
  };

  const loadCourseStats = async () => {
    try {
      const { data: courses } = await supabase
        .from('courses')
        .select('id, title')
        .order('title');

      if (!courses) return;

      const courseStatsPromises = courses.map(async (course) => {
        // Get total ACTIVE modules for this course
        const { count: totalModules } = await supabase
          .from('course_modules')
          .select('module_id', { count: 'exact', head: true })
          .eq('course_id', course.id)
          .eq('is_enabled', true);

        // Get module IDs for enabled modules in this course
        const { data: courseModuleIds } = await supabase
          .from('course_modules')
          .select('module_id')
          .eq('course_id', course.id)
          .eq('is_enabled', true);

        const moduleIds = courseModuleIds?.map(m => m.module_id) || [];

        // Get modules with audio from the enabled set
        const { data: audioModules } = await supabase
          .from('module_audio')
          .select('module_id, audio_url')
          .in('module_id', moduleIds);

        const total = totalModules || 0;
        const withAudio = audioModules?.filter(a => a.audio_url).length || 0;
        const missing = total - withAudio;

        return {
          courseId: course.id,
          courseTitle: course.title,
          totalModules: total,
          withAudio,
          missing
        };
      });

      const stats = await Promise.all(courseStatsPromises);
      setCourseStats(stats.filter(s => s.missing > 0).sort((a, b) => b.missing - a.missing));

    } catch (error) {
      console.error('Error loading course stats:', error);
    }
  };

  const handleProgress = (update: ProgressUpdate) => {
    setProgress(update);
    
    if (update.percentage === 100) {
      setGenerating(false);
      loadStats();
      toast.success(`Audio generation complete! ${update.successful} succeeded, ${update.failed} failed`);
    }
  };

  const startGeneration = async () => {
    if (!stats || stats.missing === 0) {
      toast.error('No modules need audio generation');
      return;
    }

    try {
      setGenerating(true);
      setErrorLog([]);
      
      await audioWorker.start(handleProgress);
      
      toast.success('Audio generation started', {
        description: `Processing ${stats.missing} modules`
      });

    } catch (error: any) {
      console.error('Failed to start generation:', error);
      toast.error('Failed to start audio generation', {
        description: error.message
      });
      setGenerating(false);
    }
  };

  const stopGeneration = async () => {
    try {
      await audioWorker.stop();
      setGenerating(false);
      toast.info('Audio generation stopped');
    } catch (error: any) {
      console.error('Failed to stop generation:', error);
      toast.error('Failed to stop generation');
    }
  };

  const forceCancelOperation = async () => {
    try {
      // Get stuck operation
      const { data: operations } = await supabase
        .from('audio_generation_operations')
        .select('*')
        .eq('status', 'in_progress')
        .order('started_at', { ascending: false })
        .limit(1);

      if (!operations || operations.length === 0) {
        toast.error('No active operation to cancel');
        return;
      }

      const operation = operations[0];
      
      // Force update to failed status
      const { error } = await supabase
        .from('audio_generation_operations')
        .update({
          status: 'failed',
          completed_at: new Date().toISOString(),
          error_message: 'Operation manually cancelled by admin (force stop)'
        })
        .eq('id', operation.id);

      if (error) throw error;

      setGenerating(false);
      setProgress(null);
      await loadStats();
      
      toast.success('Stuck operation cancelled successfully', {
        description: 'You can now start a fresh generation'
      });
    } catch (error: any) {
      console.error('Error cancelling operation:', error);
      toast.error('Failed to cancel operation');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Music className="h-12 w-12 animate-pulse text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading audio statistics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ADMIN CONTROLS - ALWAYS VISIBLE AT TOP */}
      <Card className="border-primary shadow-lg">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl">🎛️ Admin Controls</CardTitle>
              <CardDescription className="text-base mt-2">
                Emergency controls and system recovery tools
              </CardDescription>
            </div>
            <Button 
              onClick={loadStats}
              variant="outline"
              size="sm"
            >
              🔄 Refresh Stats
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Button
              variant="destructive"
              size="lg"
              onClick={forceCancelOperation}
              className="h-20 text-lg font-semibold"
            >
              <XCircle className="mr-2 h-6 w-6" />
              Force Cancel Operation
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={async () => {
                const { data } = await supabase
                  .from('audio_generation_operations')
                  .select('*')
                  .eq('status', 'in_progress')
                  .order('started_at', { ascending: false })
                  .limit(1);
                
                if (data && data.length > 0) {
                  toast.info('Active operation found', {
                    description: `ID: ${data[0].id} | Progress: ${data[0].processed_items}/${data[0].total_items}`
                  });
                } else {
                  toast.success('No active operations');
                }
              }}
              className="h-20 text-lg font-semibold"
            >
              <AlertCircle className="mr-2 h-6 w-6" />
              Check DB Status
            </Button>
          </div>
          
          {/* Current Status */}
          <div className="p-4 bg-muted rounded-lg">
            <p className="text-sm font-medium mb-2">Current Status:</p>
            <div className="grid grid-cols-4 gap-2 text-xs">
              <div>
                <span className="text-muted-foreground">Generating:</span>{' '}
                <Badge variant={generating ? "default" : "secondary"}>{String(generating)}</Badge>
              </div>
              <div>
                <span className="text-muted-foreground">Progress:</span>{' '}
                <Badge variant="outline">{progress ? `${progress.percentage}%` : 'N/A'}</Badge>
              </div>
              <div>
                <span className="text-muted-foreground">Missing:</span>{' '}
                <Badge variant="outline">{stats?.missing || 0}</Badge>
              </div>
              <div>
                <span className="text-muted-foreground">Loading:</span>{' '}
                <Badge variant={loading ? "destructive" : "secondary"}>{String(loading)}</Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Audio Generation</h2>
          <p className="text-muted-foreground mt-1">
            Generate OpenAI TTS audio for course modules
          </p>
        </div>
        <Music className="h-12 w-12 text-muted-foreground/20" />
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Modules</CardTitle>
            <Music className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.total.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Across all courses
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">With Audio</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{stats?.withAudio.toLocaleString()}</div>
            <Progress value={stats?.percentage} className="mt-2" />
            <p className="text-xs text-muted-foreground mt-1">
              {stats?.percentage}% complete
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Missing Audio</CardTitle>
            <AlertCircle className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{stats?.missing.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Need generation
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Est. Cost</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${estimatedCost.toFixed(2)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              For {stats?.missing} modules
            </p>
          </CardContent>
        </Card>
      </div>

      {stats && stats.missing > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Generate Missing Audio</CardTitle>
            <CardDescription>
              Use OpenAI TTS to generate audio for {stats.missing} modules without audio
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!generating ? (
              <div className="space-y-4">
                <Alert>
                  <Clock className="h-4 w-4" />
                  <AlertDescription>
                    <div className="space-y-2">
                      <p className="font-medium">Generation Details:</p>
                      <ul className="list-disc list-inside text-sm space-y-1">
                        <li>Modules to process: <strong>{stats.missing}</strong></li>
                        <li>Estimated time: <strong>~{Math.ceil(stats.missing / 10)} minutes</strong></li>
                        <li>Estimated cost: <strong>${estimatedCost.toFixed(2)}</strong></li>
                        <li>Voice: <strong>OpenAI TTS-1 (Alloy)</strong></li>
                        <li>Batch size: <strong>10 modules per batch</strong></li>
                      </ul>
                    </div>
                  </AlertDescription>
                </Alert>

                <Button 
                  onClick={startGeneration}
                  className="w-full"
                  size="lg"
                >
                  <Play className="mr-2 h-4 w-4" />
                  Generate Missing Audio
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium">Progress</span>
                    <span className="text-muted-foreground">
                      {progress?.processed || 0} / {progress?.total || 0} modules
                    </span>
                  </div>
                  <Progress value={progress?.percentage || 0} className="h-2" />
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>{progress?.percentage || 0}% complete</span>
                    <span>
                      ✓ {progress?.successful || 0} | ✗ {progress?.failed || 0}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4 p-4 bg-muted rounded-lg">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">
                      {progress?.successful || 0}
                    </div>
                    <div className="text-xs text-muted-foreground">Successful</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-orange-600">
                      {progress?.failed || 0}
                    </div>
                    <div className="text-xs text-muted-foreground">Failed</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">
                      {(progress?.total || 0) - (progress?.processed || 0)}
                    </div>
                    <div className="text-xs text-muted-foreground">Remaining</div>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button 
                    onClick={stopGeneration}
                    variant="secondary"
                    className="flex-1"
                  >
                    <Square className="mr-2 h-4 w-4" />
                    Stop Generation
                  </Button>
                  <Button 
                    onClick={forceCancelOperation}
                    variant="destructive"
                    className="flex-1"
                  >
                    <XCircle className="mr-2 h-4 w-4" />
                    Force Cancel
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="courses" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="courses">Course Breakdown</TabsTrigger>
          <TabsTrigger value="errors">Error Log</TabsTrigger>
        </TabsList>

        <TabsContent value="courses" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Courses Missing Audio</CardTitle>
              <CardDescription>
                {courseStats.length} courses have modules without audio
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                <div className="space-y-3">
                  {courseStats.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <CheckCircle2 className="h-12 w-12 mx-auto mb-2 text-green-500" />
                      <p className="font-medium">All courses have complete audio!</p>
                    </div>
                  ) : (
                    courseStats.map((course) => (
                      <div
                        key={course.courseId}
                        className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent/50 transition-colors"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{course.courseTitle}</p>
                          <p className="text-sm text-muted-foreground">
                            {course.withAudio}/{course.totalModules} modules with audio
                          </p>
                        </div>
                        <div className="flex items-center gap-3 ml-4">
                          <Progress 
                            value={(course.withAudio / course.totalModules) * 100} 
                            className="w-24"
                          />
                          <Badge variant={course.missing > 10 ? "destructive" : "secondary"}>
                            {course.missing} missing
                          </Badge>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="errors">
          <Card>
            <CardHeader>
              <CardTitle>Generation Errors</CardTitle>
              <CardDescription>
                Errors encountered during audio generation
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                {errorLog.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <CheckCircle2 className="h-12 w-12 mx-auto mb-2" />
                    <p>No errors recorded</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {errorLog.map((error, index) => (
                      <Alert key={index} variant="destructive">
                        <XCircle className="h-4 w-4" />
                        <AlertDescription className="font-mono text-xs">
                          {error}
                        </AlertDescription>
                      </Alert>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AudioGenerationDashboard;
